// Audio utilities for Gemini Live API
// Simplified implementation for reliable audio capture and playback

/**
 * Audio Capture - Captures microphone as 16kHz 16-bit PCM
 */
export class AudioCapture {
    private audioContext: AudioContext | null = null;
    private mediaStream: MediaStream | null = null;
    private workletNode: AudioWorkletNode | null = null;
    private sourceNode: MediaStreamAudioSourceNode | null = null;
    private callback: ((data: ArrayBuffer) => void) | null = null;

    private analyserNode: AnalyserNode | null = null;
    private volumeCallback: ((volume: number) => void) | null = null;
    private volumeInterval: number | null = null;

    async start(onAudioData: (data: ArrayBuffer) => void, onVolumeChange?: (volume: number) => void): Promise<void> {
        this.callback = onAudioData;
        this.volumeCallback = onVolumeChange || null;

        try {
            // Get microphone
            this.mediaStream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    channelCount: 1,
                    sampleRate: 16000,
                    echoCancellation: true,
                    noiseSuppression: true,
                },
            });

            // Create audio context at 16kHz
            this.audioContext = new AudioContext({ sampleRate: 16000 });

            // CRITICAL: Resume if suspended (browser autoplay policy)
            if (this.audioContext.state === 'suspended') {
                await this.audioContext.resume();
            }

            // Load worklet
            await this.audioContext.audioWorklet.addModule('/pcm-capture-processor.js');

            // Create nodes
            this.sourceNode = this.audioContext.createMediaStreamSource(this.mediaStream);
            this.workletNode = new AudioWorkletNode(this.audioContext, 'pcm-capture-processor');

            // Volume Analysis
            this.analyserNode = this.audioContext.createAnalyser();
            this.analyserNode.fftSize = 256;
            this.analyserNode.smoothingTimeConstant = 0.5;
            this.sourceNode.connect(this.analyserNode);

            this.startVolumeAnalysis();

            // Handle audio data from worklet
            this.workletNode.port.onmessage = (event) => {
                if (this.callback && event.data) {
                    // event.data is Int16Array, convert to ArrayBuffer
                    const buffer = event.data instanceof Int16Array
                        ? event.data.buffer
                        : event.data;
                    this.callback(buffer);
                }
            };

            // Connect: mic -> worklet -> destination (for monitoring)
            this.sourceNode.connect(this.workletNode);
            // Don't connect to destination to avoid feedback

            console.log('[AudioCapture] Started');
        } catch (error) {
            console.error('[AudioCapture] Error:', error);
            throw error;
        }
    }

    private startVolumeAnalysis() {
        if (!this.analyserNode) return;
        const dataArray = new Uint8Array(this.analyserNode.frequencyBinCount);

        this.volumeInterval = window.setInterval(() => {
            if (!this.analyserNode || !this.volumeCallback) return;

            this.analyserNode.getByteTimeDomainData(dataArray);

            let sum = 0;
            for (let i = 0; i < dataArray.length; i++) {
                // Convert 0-255 range to -1 to 1
                const x = (dataArray[i] - 128) / 128.0;
                sum += x * x;
            }
            const rms = Math.sqrt(sum / dataArray.length);
            // Amplify a bit for better visual effect and clamp to 0-1
            const volume = Math.min(1, rms * 5);

            this.volumeCallback(volume);
        }, 50); // Update every 50ms
    }

    stop(): void {
        if (this.volumeInterval) {
            clearInterval(this.volumeInterval);
            this.volumeInterval = null;
        }
        if (this.analyserNode) {
            this.analyserNode.disconnect();
            this.analyserNode = null;
        }
        if (this.workletNode) {
            this.workletNode.disconnect();
            this.workletNode = null;
        }
        if (this.sourceNode) {
            this.sourceNode.disconnect();
            this.sourceNode = null;
        }
        if (this.mediaStream) {
            this.mediaStream.getTracks().forEach(t => t.stop());
            this.mediaStream = null;
        }
        if (this.audioContext) {
            this.audioContext.close();
            this.audioContext = null;
        }
        this.callback = null;
        console.log('[AudioCapture] Stopped');
    }
}

/**
 * Audio Player - Plays 24kHz 16-bit PCM from Gemini
 * Supports interruption for conversational AI
 */
export class AudioPlayer {
    private audioContext: AudioContext;
    private queue: AudioBuffer[] = [];
    private isPlaying = false;
    private onPlaybackEnd: (() => void) | null = null;
    private currentSource: AudioBufferSourceNode | null = null;
    private interrupted = false;

    constructor() {
        this.audioContext = new AudioContext({ sampleRate: 24000 });
    }

    setOnPlaybackEnd(callback: () => void): void {
        this.onPlaybackEnd = callback;
    }

    async play(pcmData: ArrayBuffer): Promise<void> {
        // If interrupted, ignore new audio until reset
        if (this.interrupted) {
            console.log('[AudioPlayer] Ignoring audio - interrupted');
            return;
        }

        if (!pcmData || pcmData.byteLength === 0) {
            console.warn('[AudioPlayer] Empty audio data');
            return;
        }

        try {
            // Resume audio context if suspended (browser autoplay policy)
            if (this.audioContext.state === 'suspended') {
                console.log('[AudioPlayer] Context suspended, resuming...');
                await this.audioContext.resume();
                console.log('[AudioPlayer] Context resumed:', this.audioContext.state);
            }

            // Convert 16-bit PCM to Float32
            const int16 = new Int16Array(pcmData);
            const float32 = new Float32Array(int16.length);

            for (let i = 0; i < int16.length; i++) {
                float32[i] = int16[i] / 32768.0;
            }

            console.log(`[AudioPlayer] Queueing buffer: ${float32.length} samples, rate: ${this.audioContext.sampleRate}`);

            // Create audio buffer
            const buffer = this.audioContext.createBuffer(1, float32.length, 24000);
            buffer.getChannelData(0).set(float32);

            // Queue it
            this.queue.push(buffer);

            // Start playing if not already
            if (!this.isPlaying) {
                this.playNext();
            }
        } catch (error) {
            console.error('[AudioPlayer] Error:', error);
        }
    }

    private playNext(): void {
        if (this.interrupted || this.queue.length === 0) {
            this.isPlaying = false;
            this.currentSource = null;
            if (this.onPlaybackEnd && !this.interrupted) {
                this.onPlaybackEnd();
            }
            return;
        }

        this.isPlaying = true;
        const buffer = this.queue.shift()!;

        const source = this.audioContext.createBufferSource();
        source.buffer = buffer;

        // Audio processing chain for better quality
        // 1. Low-pass filter to reduce high-frequency static/artifacts
        const lowPassFilter = this.audioContext.createBiquadFilter();
        lowPassFilter.type = 'lowpass';
        lowPassFilter.frequency.value = 10000; // Higher cutoff for cleaner sound
        lowPassFilter.Q.value = 0.5; // Very gentle rolloff

        // 2. Slight gain boost for clarity
        const gainNode = this.audioContext.createGain();
        gainNode.gain.value = 1.2; // 20% boost

        // Connect: source -> lowpass -> gain -> destination
        source.connect(lowPassFilter);
        lowPassFilter.connect(gainNode);
        gainNode.connect(this.audioContext.destination);

        // Track current source for interruption
        this.currentSource = source;

        source.onended = () => {
            if (this.currentSource === source) {
                this.currentSource = null;
            }
            this.playNext();
        };

        source.start();
    }

    /**
     * Interrupt playback immediately - stops current audio and clears queue
     * Call this when user starts speaking to allow them to cut off the AI
     */
    interrupt(): void {
        console.log('[AudioPlayer] Interrupting playback');
        this.interrupted = true;
        this.queue = [];

        if (this.currentSource) {
            try {
                this.currentSource.stop();
            } catch (e) {
                // Source might already be stopped
            }
            this.currentSource = null;
        }

        this.isPlaying = false;
    }

    /**
     * Reset interrupt state - call this after handling the interruption
     */
    resetInterrupt(): void {
        this.interrupted = false;
    }

    /**
     * Check if player was interrupted
     */
    wasInterrupted(): boolean {
        return this.interrupted;
    }

    stop(): void {
        this.queue = [];
        this.isPlaying = false;
        this.interrupted = false;
        if (this.currentSource) {
            try {
                this.currentSource.stop();
            } catch (e) {
                // Source might already be stopped
            }
            this.currentSource = null;
        }
    }

    isCurrentlyPlaying(): boolean {
        return this.isPlaying;
    }

    /**
     * Explicitly resume audio context (call this from a user gesture)
     */
    async resume(): Promise<void> {
        if (this.audioContext.state === 'suspended') {
            console.log('[AudioPlayer] Eagerly resuming context from user gesture');
            await this.audioContext.resume();
        }
    }

    /**
     * Play a simple tone (for notifications like connection dropping)
     */
    playTone(frequency: number = 440, type: OscillatorType = 'sine', duration: number = 0.5): void {
        try {
            const osc = this.audioContext.createOscillator();
            const gain = this.audioContext.createGain();

            osc.type = type;
            osc.frequency.setValueAtTime(frequency, this.audioContext.currentTime);

            gain.gain.setValueAtTime(0.1, this.audioContext.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.001, this.audioContext.currentTime + duration);

            osc.connect(gain);
            gain.connect(this.audioContext.destination);

            osc.start();
            osc.stop(this.audioContext.currentTime + duration);
        } catch (e) {
            console.warn('[AudioPlayer] Failed to play tone:', e);
        }
    }
}

/**
 * Voice Activity Detector - Monitors microphone for speech to enable hands-free interruption
 * Uses audio level analysis to detect when user starts speaking
 */
export class VoiceActivityDetector {
    private audioContext: AudioContext | null = null;
    private mediaStream: MediaStream | null = null;
    private analyserNode: AnalyserNode | null = null;
    private sourceNode: MediaStreamAudioSourceNode | null = null;
    private isMonitoring = false;
    private checkInterval: number | null = null;

    // Callbacks
    private onSpeechStart: (() => void) | null = null;
    private onSpeechEnd: (() => void) | null = null;

    // VAD settings
    private threshold = 0.015; // Audio level threshold (0-1)
    private speechFrames = 0;  // Consecutive frames with speech
    private silenceFrames = 0; // Consecutive frames without speech
    private isSpeaking = false;
    private readonly SPEECH_START_FRAMES = 3;  // Frames needed to trigger speech start
    private readonly SPEECH_END_FRAMES = 15;   // Frames needed to trigger speech end

    /**
     * Start monitoring microphone for voice activity
     */
    async start(onSpeechStart: () => void, onSpeechEnd?: () => void): Promise<void> {
        if (this.isMonitoring) return;

        this.onSpeechStart = onSpeechStart;
        this.onSpeechEnd = onSpeechEnd || null;

        try {
            this.mediaStream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    channelCount: 1,
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                },
            });

            this.audioContext = new AudioContext();
            this.analyserNode = this.audioContext.createAnalyser();
            this.analyserNode.fftSize = 512;
            this.analyserNode.smoothingTimeConstant = 0.8;

            this.sourceNode = this.audioContext.createMediaStreamSource(this.mediaStream);
            this.sourceNode.connect(this.analyserNode);

            this.isMonitoring = true;
            this.startVADLoop();

            console.log('[VAD] Started monitoring');
        } catch (error) {
            console.error('[VAD] Failed to start:', error);
            throw error;
        }
    }

    private startVADLoop(): void {
        const dataArray = new Float32Array(this.analyserNode!.fftSize);

        const checkAudio = () => {
            if (!this.isMonitoring || !this.analyserNode) return;

            this.analyserNode.getFloatTimeDomainData(dataArray);

            // Calculate RMS (root mean square) for audio level
            let sum = 0;
            for (let i = 0; i < dataArray.length; i++) {
                sum += dataArray[i] * dataArray[i];
            }
            const rms = Math.sqrt(sum / dataArray.length);

            // Check if audio level exceeds threshold
            if (rms > this.threshold) {
                this.speechFrames++;
                this.silenceFrames = 0;

                if (!this.isSpeaking && this.speechFrames >= this.SPEECH_START_FRAMES) {
                    this.isSpeaking = true;
                    console.log('[VAD] Speech detected - level:', rms.toFixed(4));
                    this.onSpeechStart?.();
                }
            } else {
                this.silenceFrames++;
                this.speechFrames = 0;

                if (this.isSpeaking && this.silenceFrames >= this.SPEECH_END_FRAMES) {
                    this.isSpeaking = false;
                    console.log('[VAD] Speech ended');
                    this.onSpeechEnd?.();
                }
            }
        };

        // Check every 50ms
        this.checkInterval = window.setInterval(checkAudio, 50);
    }

    /**
     * Pause VAD temporarily (e.g., while user is actively recording)
     */
    pause(): void {
        if (this.checkInterval) {
            window.clearInterval(this.checkInterval);
            this.checkInterval = null;
        }
        this.speechFrames = 0;
        this.silenceFrames = 0;
        this.isSpeaking = false;
    }

    /**
     * Resume VAD after pause
     */
    resume(): void {
        if (this.isMonitoring && !this.checkInterval) {
            this.startVADLoop();
        }
    }

    /**
     * Stop VAD completely and release microphone
     */
    stop(): void {
        this.isMonitoring = false;

        if (this.checkInterval) {
            window.clearInterval(this.checkInterval);
            this.checkInterval = null;
        }

        if (this.sourceNode) {
            this.sourceNode.disconnect();
            this.sourceNode = null;
        }

        if (this.analyserNode) {
            this.analyserNode.disconnect();
            this.analyserNode = null;
        }

        if (this.mediaStream) {
            this.mediaStream.getTracks().forEach(t => t.stop());
            this.mediaStream = null;
        }

        if (this.audioContext) {
            this.audioContext.close();
            this.audioContext = null;
        }

        this.onSpeechStart = null;
        this.onSpeechEnd = null;
        console.log('[VAD] Stopped');
    }

    /**
     * Adjust sensitivity (higher = more sensitive, lower = less sensitive)
     */
    setSensitivity(level: 'low' | 'medium' | 'high'): void {
        switch (level) {
            case 'low':
                this.threshold = 0.03;
                break;
            case 'medium':
                this.threshold = 0.015;
                break;
            case 'high':
                this.threshold = 0.008;
                break;
        }
        console.log('[VAD] Sensitivity set to:', level, 'threshold:', this.threshold);
    }

    isActive(): boolean {
        return this.isMonitoring;
    }
}
